import React, { useState } from "react";
import { Box, Button, Stack, Textarea, Typography } from "@mui/joy";
import { theme } from "styles/theme";
import { taskEditorScrollbarSx } from "../utils/taskEditorScrollbarSx";
import type {
  ActionRequest,
  AssistantStep,
  Decision,
  PendingApprovalData,
  ReviewConfig,
} from "./types";
import type {
  TaskEditorActionComponent,
  TaskEditorBlock,
  TaskEditorDescription,
} from "../utils/taskEditorUtils";
import {
  getTaskEditorBlockDisplayLabels,
  normalizeTaskEditorBlocks,
} from "../utils/taskEditorUtils";
import { alignBlocks, type BlockDiffRow } from "../utils/alignBlocks";
import { computeLineDiff, DIFF_BG } from "../jsonCodeView";
import {
  asBlocks,
  asPlanText,
  getActionKind,
  splitReviewFeedback,
} from "./approvalContent";
import { Markdown } from "./Markdown";

const MONO = "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace";

// 채팅 인풋(chatInput.tsx)과 동일한 얇은 포커스 — Joy 의 2px 포커스 링(::before)을
// 통째로 끄고, 1px 보더만 직접 그려 포커스 시 색만 Border.Highlight 로 바꾼다.
const THIN_FOCUS = {
  "--Textarea-focusedHighlight": "transparent",
  border: `1px solid ${theme.vars.palette.Border.Weak}`,
  "&::before": { display: "none" },
  "&:focus-within": { borderColor: theme.vars.palette.Border.Highlight },
} as const;

// propose_plan / update_task → 사람이 읽을 짧은 라벨 (감사 로그용).
function actionDisplayName(name: string): string {
  if (name === "propose_plan") return "계획";
  if (name === "update_task") return "활동지";
  return name;
}

// =========================
// ApprovalDecisionRow — 사용자가 내린 결정 표시
// =========================

export function ApprovalDecisionRow({
  step,
}: {
  step: Extract<AssistantStep, { kind: "approval_decision" }>;
}) {
  const decisionLabel =
    step.decision.type === "approve"
      ? "✓ 승인"
      : step.decision.type === "edit"
        ? "✎ 직접 수정"
        : step.decision.type === "revise"
          ? "↻ 수정 요청"
          : "거부";
  // revise/reject 는 거절이 아니라 자연어 재작성 요청·turn 종료 — danger(빨강) 어감 안 쓴다.
  // reject 는 사용자가 의도적으로 끄는 보조 액션이라 더 차분한 Weak 색으로.
  const decisionColor =
    step.decision.type === "approve"
      ? "#15803d"
      : step.decision.type === "edit"
        ? theme.vars.palette.primary.solid
        : step.decision.type === "revise"
          ? theme.vars.palette.Content.Medium
          : theme.vars.palette.Content.Weak;
  const trailingText =
    step.decision.type === "revise"
      ? step.decision.message
      : step.decision.type === "reject"
        ? step.decision.reason
        : undefined;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        px: "8px",
        py: "4px",
        fontSize: "11px",
        color: theme.vars.palette.Content.Weak,
      }}
    >
      <Box
        component="span"
        sx={{
          color: decisionColor,
          fontWeight: 700,
        }}
      >
        {decisionLabel}
      </Box>
      <Box
        component="span"
        sx={{
          color: theme.vars.palette.Content.Medium,
        }}
      >
        {actionDisplayName(step.originalAction.name)}
      </Box>
      {trailingText && (
        <Box
          component="span"
          sx={{
            fontStyle: "italic",
            color: theme.vars.palette.Content.Weak,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          “{trailingText}”
        </Box>
      )}
    </Box>
  );
}

// =========================
// ApprovalCard
// =========================

// 카드 헤더 라벨 — action 종류 / 검토 의견 유무 / 계획 선행 여부로 결정.
function cardHeaderLabel(
  data: PendingApprovalData,
  precededByPlan: boolean
): string {
  const first = data.action_requests[0];
  const kind = first ? getActionKind(first.name) : "unknown";
  if (kind === "plan") return "📋 계획 검토 · 1/2단계";
  if (kind === "task") {
    const hasReview =
      !!first && splitReviewFeedback(first.description).review !== null;
    if (hasReview) return "활동지 검토 (개선안)";
    if (precededByPlan) return "활동지 검토 · 2/2단계";
    return "활동지 검토";
  }
  return "승인 필요";
}

export function ApprovalCard({
  data,
  onDecide,
  precededByPlan = false,
  baseBlocks,
}: {
  data: PendingApprovalData;
  onDecide: (decisions: Decision[]) => void;
  // 같은 turn 에서 propose_plan 승인이 선행됐는지 — update_task 카드의 2/2 단계 표기용.
  precededByPlan?: boolean;
  // 이 turn 시작 시점의 blocks — replace_text 변경의 "이전 텍스트" 표시용 (없으면 새 내용만).
  baseBlocks?: TaskEditorBlock[];
}) {
  return (
    <Stack
      spacing={1.5}
      sx={{
        p: "12px",
        mt: "4px",
        borderRadius: "8px",
        backgroundColor: theme.vars.palette.Background.Level1,
        border: `1px solid ${theme.vars.palette.primary.solid}`,
        animation: "approvalPop 0.3s ease",
        "@keyframes approvalPop": {
          from: { opacity: 0, transform: "scale(0.98)" },
          to: { opacity: 1, transform: "scale(1)" },
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          sx={{
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            backgroundColor: theme.vars.palette.primary.solid,
            animation: "pulse 1.5s ease infinite",
            "@keyframes pulse": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.4 },
            },
          }}
        />
        <Typography
          level="body-xs"
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            color: theme.vars.palette.primary.solid,
            letterSpacing: "0.04em",
          }}
        >
          {cardHeaderLabel(data, precededByPlan)}
        </Typography>
        {/* 게이트된 도구명(verbatim) — 이 카드가 특정 tool 호출의 승인(HITL)임을 드러냄. */}
        {data.action_requests[0]?.name && (
          <Box
            component="span"
            sx={{
              fontSize: "10px",
              color: theme.vars.palette.Content.Weak,
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            }}
          >
            {data.action_requests[0].name}
          </Box>
        )}
      </Stack>
      {data.action_requests.map((action, idx) => (
        <ApprovalItem
          key={idx}
          action={action}
          baseBlocks={baseBlocks}
          config={data.review_configs[idx]}
          onApprove={() => onDecide([{ type: "approve" }])}
          onEdit={(editedArgs) =>
            onDecide([
              {
                type: "edit",
                edited_action: { name: action.name, args: editedArgs },
              },
            ])
          }
          onRevise={(message) => onDecide([{ type: "revise", message }])}
          onReject={(reason) =>
            onDecide([{ type: "reject", ...(reason ? { reason } : {}) }])
          }
        />
      ))}
    </Stack>
  );
}

// args 의 top-level string field 중 JSON 객체/배열로 parse 되는 것은 inline 객체로
// 펼쳐서 보여준다. 레거시 write_file.content 처럼 stringified JSON 을 그대로 escape 된
// 한 줄 string 으로 보여주는 것을 막기 위함. 저장 시 inlinedKeys 로 다시 string 화한다.
function expandJsonStringFields(args: Record<string, unknown>): {
  displayText: string;
  inlinedKeys: string[];
} {
  const expanded: Record<string, unknown> = {};
  const inlinedKeys: string[] = [];
  for (const [key, value] of Object.entries(args)) {
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        if (parsed !== null && typeof parsed === "object") {
          expanded[key] = parsed;
          inlinedKeys.push(key);
          continue;
        }
      } catch {
        // not JSON — fall through
      }
    }
    expanded[key] = value;
  }
  return { displayText: JSON.stringify(expanded, null, 2), inlinedKeys };
}

function collapseJsonStringFields(
  edited: Record<string, unknown>,
  inlinedKeys: string[]
): Record<string, unknown> {
  const result: Record<string, unknown> = { ...edited };
  for (const key of inlinedKeys) {
    const v = result[key];
    if (v !== undefined && typeof v !== "string") {
      result[key] = JSON.stringify(v, null, 2);
    }
  }
  return result;
}

function ApprovalItem({
  action,
  config,
  baseBlocks,
  onApprove,
  onEdit,
  onRevise,
  onReject,
}: {
  action: ActionRequest;
  config?: ReviewConfig;
  baseBlocks?: TaskEditorBlock[];
  onApprove: () => void;
  onEdit: (args: Record<string, unknown>) => void;
  onRevise: (message: string) => void;
  onReject: (reason: string) => void;
}) {
  const kind = getActionKind(action.name);
  const [mode, setMode] = useState<"idle" | "edit" | "revise" | "reject">(
    "idle"
  );
  const [planText, setPlanText] = useState(() => asPlanText(action.args) ?? "");
  const [{ argsText, inlinedKeys }, setArgsState] = useState(() => {
    const { displayText, inlinedKeys: keys } = expandJsonStringFields(
      action.args
    );
    return { argsText: displayText, inlinedKeys: keys };
  });
  const [reviseMessage, setReviseMessage] = useState("");
  const [rejectReason, setRejectReason] = useState("");

  const allowed = config?.allowed_decisions ?? [
    "approve",
    "edit",
    "revise",
    "reject",
  ];

  // task(update_task) 의 description 에는 task_reviewer 검토 의견이 동봉될 수 있다.
  const { body, review } =
    kind === "task"
      ? splitReviewFeedback(action.description)
      : { body: action.description, review: null };

  return (
    <Stack spacing={1}>
      {/* 본문 설명 — 계획은 인용 박스로 따로 보여주므로 중복 출력하지 않는다. */}
      {kind !== "plan" && body && (
        <Typography
          level="body-sm"
          sx={{
            fontSize: "13px",
            color: theme.vars.palette.Content.Medium,
            lineHeight: 1.5,
            whiteSpace: "pre-wrap",
          }}
        >
          {body}
        </Typography>
      )}

      {/* 미리보기 — action 종류별 분기 */}
      {kind === "plan" && (
        <Stack spacing={0.75}>
          <Typography
            level="body-xs"
            sx={{
              fontSize: "12px",
              color: theme.vars.palette.Content.Medium,
            }}
          >
            이 구성으로 만들까요?
          </Typography>
          <PlanBox text={asPlanText(action.args) ?? action.description} />
        </Stack>
      )}
      {kind === "task" && (
        <>
          {review && <ReviewBox text={review} />}
          <TaskChangePreview action={action} baseBlocks={baseBlocks} />
        </>
      )}
      {kind === "unknown" && <GenericArgsPreview args={action.args} />}

      {mode === "idle" && (
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          alignItems="center"
          sx={{ flexWrap: "wrap" }}
        >
          {/* 거부 — 사용자가 의도적으로 끄는 보조 액션. 직접 수정과 같은 outlined neutral 로 톤 맞춤. */}
          {allowed.includes("reject") && (
            <Button
              size="sm"
              variant="outlined"
              color="neutral"
              onClick={() => setMode("reject")}
              sx={{ minHeight: "30px", fontSize: "12px" }}
            >
              ✕ 거부
            </Button>
          )}
          {allowed.includes("edit") && (
            <Button
              size="sm"
              variant="outlined"
              color="neutral"
              onClick={() => setMode("edit")}
              sx={{ minHeight: "30px", fontSize: "12px" }}
            >
              ✎ 직접 수정
            </Button>
          )}
          {allowed.includes("revise") && (
            <Button
              size="sm"
              variant="outlined"
              color="primary"
              onClick={() => setMode("revise")}
              sx={{ minHeight: "30px", fontSize: "12px" }}
            >
              ↻ 수정 요청
            </Button>
          )}
          {allowed.includes("approve") && (
            <Button
              size="sm"
              variant="solid"
              color="primary"
              onClick={onApprove}
              sx={{ minHeight: "30px", fontSize: "12px", fontWeight: 600 }}
            >
              ✓ 승인
            </Button>
          )}
        </Stack>
      )}

      {/* edit — 계획은 텍스트 편집, 그 외(활동지/미지)는 JSON 편집 */}
      {mode === "edit" && kind === "plan" && (
        <Stack spacing={1}>
          <Textarea
            value={planText}
            onChange={(e) => setPlanText(e.target.value)}
            minRows={4}
            maxRows={14}
            sx={{ fontSize: "13px", lineHeight: 1.6, ...THIN_FOCUS }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              onClick={() => setMode("idle")}
              sx={{ minHeight: "28px", fontSize: "12px" }}
            >
              취소
            </Button>
            <Button
              size="sm"
              variant="solid"
              color="primary"
              onClick={() => onEdit({ plan: planText })}
              sx={{ minHeight: "28px", fontSize: "12px" }}
            >
              수정 후 적용
            </Button>
          </Stack>
        </Stack>
      )}

      {mode === "edit" && kind !== "plan" && (
        <Stack spacing={1}>
          <Textarea
            value={argsText}
            onChange={(e) =>
              setArgsState((prev) => ({
                ...prev,
                argsText: e.target.value,
              }))
            }
            minRows={6}
            maxRows={14}
            sx={{ fontFamily: MONO, fontSize: "11px", ...THIN_FOCUS }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              onClick={() => setMode("idle")}
              sx={{ minHeight: "28px", fontSize: "12px" }}
            >
              취소
            </Button>
            <Button
              size="sm"
              variant="solid"
              color="primary"
              onClick={() => {
                try {
                  const parsed = JSON.parse(argsText);
                  onEdit(collapseJsonStringFields(parsed, inlinedKeys));
                } catch {
                  // ignore parse error — leave UI open
                }
              }}
              sx={{ minHeight: "28px", fontSize: "12px" }}
            >
              수정 후 적용
            </Button>
          </Stack>
        </Stack>
      )}

      {/* 수정 요청(revise) — 자연어로 "이렇게 고쳐서 다시"를 agent 에 전달 */}
      {mode === "revise" && (
        <Stack spacing={1}>
          <Textarea
            value={reviseMessage}
            onChange={(e) => setReviseMessage(e.target.value)}
            placeholder="어떻게 바꿀까요?"
            minRows={2}
            maxRows={5}
            sx={{ fontSize: "13px", ...THIN_FOCUS }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              onClick={() => setMode("idle")}
              sx={{ minHeight: "28px", fontSize: "13px" }}
            >
              취소
            </Button>
            <Button
              size="sm"
              variant="solid"
              color="primary"
              disabled={!reviseMessage.trim()}
              onClick={() => onRevise(reviseMessage.trim())}
              sx={{ minHeight: "28px", fontSize: "13px" }}
            >
              수정 요청 보내기
            </Button>
          </Stack>
        </Stack>
      )}

      {/* 거부(reject) — turn 종료. 사유는 선택. */}
      {mode === "reject" && (
        <Stack spacing={1}>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="거부 사유 (선택). 비워둬도 됩니다."
            minRows={2}
            maxRows={5}
            sx={{ fontSize: "13px", ...THIN_FOCUS }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              onClick={() => setMode("idle")}
              sx={{ minHeight: "28px", fontSize: "13px" }}
            >
              취소
            </Button>
            <Button
              size="sm"
              variant="solid"
              color="danger"
              onClick={() => onReject(rejectReason.trim())}
              sx={{ minHeight: "28px", fontSize: "13px" }}
            >
              거부하기
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}

// 계획(propose_plan) 텍스트 — 균일한 1px 보더 인용 박스, 줄바꿈 보존.
// (카드 전체가 이미 primary 보더라 왼쪽 강조 띠는 과해서 제거)
function PlanBox({ text }: { text: string }) {
  return (
    <Box
      sx={{
        borderRadius: "6px",
        border: `1px solid ${theme.vars.palette.Border.Weak}`,
        backgroundColor: theme.vars.palette.Background.Level2,
        p: "10px 12px",
        fontSize: "13px",
        lineHeight: 1.6,
        color: theme.vars.palette.Content.Strong,
        wordBreak: "break-word",
      }}
    >
      <Markdown text={text} />
    </Box>
  );
}

// task_reviewer 검토 의견 — amber 좌측 보더로 본문/계획과 시각 구분.
function ReviewBox({ text }: { text: string }) {
  return (
    <Box
      sx={{
        borderRadius: "6px",
        border: "1px solid rgba(217, 119, 6, 0.3)",
        backgroundColor: "rgba(217, 119, 6, 0.06)",
        p: "8px 10px",
      }}
    >
      <Box
        sx={{
          fontSize: "11px",
          fontWeight: 700,
          color: "#b45309",
          mb: "4px",
          letterSpacing: "0.02em",
        }}
      >
        📝 task_reviewer 검토 의견
      </Box>
      <Box
        sx={{
          fontSize: "12px",
          lineHeight: 1.6,
          color: theme.vars.palette.Content.Medium,
          wordBreak: "break-word",
        }}
      >
        <Markdown text={text} />
      </Box>
    </Box>
  );
}

// update_task 의 blocks 미리보기 — 검토가 가능하도록 블록별 전체 텍스트를 보여준다
// (잘라내지 않음). 기본은 펼친 상태, 길면 헤더 토글로 접을 수 있다.
// update_task 미리보기 — surgical 편집이면 변경분만 diff 로, 아니면(신규·전면 재작성·구버전 BE)
// 전체 blocks preview 로 폴백한다. authoritative 본문은 어느 쪽이든 args.blocks.
function TaskChangePreview({
  action,
  baseBlocks,
}: {
  action: ActionRequest;
  baseBlocks?: TaskEditorBlock[];
}) {
  const afterBlocks = asBlocks(action.args);
  if (afterBlocks && baseBlocks) {
    // base(taskBefore)는 이미 정규화됐지만 after 는 서버 raw payload — 같은 정규화로
    // 맞춰야 빈 기본값(optional:false·is_correct:false 등) 차이로 안 바뀐 블록이
    // "modified" 로 떠서 diff 가 폭발하는 것을 막는다. args 는 raw 그대로 둔다
    // (전체 미리보기·직접 수정 왕복은 권위 payload 를 써야 하므로).
    return (
      <BlockDiffView
        base={baseBlocks}
        after={normalizeTaskEditorBlocks(afterBlocks)}
        args={action.args}
      />
    );
  }
  return <BlocksPreview args={action.args} />;
}

// 블록을 사람이 읽는 여러 줄 텍스트로 변환 — 라인 단위 diff 의 입력. description
// 텍스트(줄바꿈 보존) + 부가요소 요약 한 줄 + action 요약 한 줄.
function blockToReadableText(block: TaskEditorBlock): string {
  const lines = [block.description.content];
  const extra = describeDescriptionExtra(block.description);
  if (extra) lines.push(extra);
  if (block.action) lines.push(describeAction(block.action));
  return lines.join("\n");
}

// 한 블록의 before→after 를 읽기 쉬운 텍스트 라인 diff 로 (jsonCodeView 의 +/- 색 재사용,
// JSON 하이라이트 없이 평문 — 한글 본문이라 적합).
function ReadableTextDiff({
  before,
  after,
}: {
  before: TaskEditorBlock;
  after: TaskEditorBlock;
}) {
  const lines = computeLineDiff(
    blockToReadableText(before),
    blockToReadableText(after)
  );
  return (
    <Box
      sx={{
        mt: "4px",
        fontSize: "11px",
        lineHeight: 1.5,
        maxHeight: "240px",
        overflow: "auto",
        ...taskEditorScrollbarSx,
      }}
    >
      {lines.map((line, i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            gap: "6px",
            px: "6px",
            backgroundColor:
              line.kind === "add"
                ? DIFF_BG.add
                : line.kind === "del"
                  ? DIFF_BG.del
                  : "transparent",
            color:
              line.kind === "same"
                ? theme.vars.palette.Content.Weak
                : theme.vars.palette.Content.Strong,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          <Box
            component="span"
            sx={{
              flexShrink: 0,
              width: "1ch",
              userSelect: "none",
              color: theme.vars.palette.Content.Weak,
            }}
          >
            {line.kind === "add" ? "+" : line.kind === "del" ? "-" : ""}
          </Box>
          <Box component="span">{line.text || " "}</Box>
        </Box>
      ))}
    </Box>
  );
}

// 변경 행 라벨 (kind 별 아이콘·색·문구). 라벨은 표시 번호 규칙(메인 카운트,
// 세그먼트는 "블록 K · 옵션 M · J") — 에디터 카드 라벨과 동일 기준.
function rowLabel(
  row: BlockDiffRow,
  baseLabels: string[],
  afterLabels: string[]
): {
  icon: string;
  text: string;
  color: string;
} {
  const add = theme.vars.palette.primary.solid;
  const del = "#dc2626"; // 삭제 강조용 빨강 (ApprovalDecisionRow 의 하드코드 색과 동일 패턴)
  const mod = theme.vars.palette.Content.Medium;
  const baseLabel = (index: number) => baseLabels[index] ?? `블록 ${index + 1}`;
  const afterLabel = (index: number) =>
    afterLabels[index] ?? `블록 ${index + 1}`;
  switch (row.kind) {
    case "modified":
      return {
        icon: "✏",
        text: `${afterLabel(row.afterIndex)} 수정`,
        color: mod,
      };
    case "added":
      return {
        icon: "＋",
        text: `${afterLabel(row.afterIndex)} 추가`,
        color: add,
      };
    case "removed":
      return {
        icon: "－",
        text: `${baseLabel(row.beforeIndex)} 삭제`,
        color: del,
      };
    case "moved":
      return {
        icon: "⇄",
        text: `${baseLabel(row.beforeIndex)} → ${afterLabel(row.afterIndex)} 이동`,
        color: add,
      };
  }
}

// Codex/Claude Code 식 diff 승인 — base↔after 정렬로 변경 블록만 보여준다(수정=before→after 텍스트
// diff, 추가/삭제=블록 내용, 이동=위치). 전체 활동지(미변경 포함)는 토글로 펼친다.
function BlockDiffView({
  base,
  after,
  args,
}: {
  base: TaskEditorBlock[];
  after: TaskEditorBlock[];
  args: Record<string, unknown>;
}) {
  const [showAll, setShowAll] = useState(false);
  const rows = alignBlocks(base, after);
  const baseLabels = getTaskEditorBlockDisplayLabels(base);
  const afterLabels = getTaskEditorBlockDisplayLabels(after);
  // byte-동일(noop 커밋) — 변경 행이 없으면 전체 미리보기로 폴백.
  if (rows.length === 0) return <BlocksPreview args={args} />;
  return (
    <Box>
      <Box
        sx={{
          fontSize: "10px",
          fontWeight: 600,
          color: theme.vars.palette.Content.Weak,
          letterSpacing: "0.04em",
          mb: "4px",
        }}
      >
        변경 {rows.length}건
      </Box>
      <Stack
        sx={{
          borderRadius: "6px",
          border: `1px solid ${theme.vars.palette.Border.Weak}`,
          backgroundColor: theme.vars.palette.Background.Level2,
          overflow: "hidden",
        }}
      >
        {rows.map((row, i) => {
          const { icon, text, color } = rowLabel(row, baseLabels, afterLabels);
          return (
            <Box
              key={i}
              sx={{
                p: "6px 10px",
                borderBottom:
                  i === rows.length - 1
                    ? 0
                    : `1px solid ${theme.vars.palette.Border.Weak}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Box
                  component="span"
                  sx={{
                    fontFamily: MONO,
                    fontSize: "11px",
                    color,
                    flexShrink: 0,
                  }}
                >
                  {icon}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: "12px",
                    color: theme.vars.palette.Content.Strong,
                  }}
                >
                  {text}
                </Box>
              </Box>
              {row.kind === "modified" && (
                <ReadableTextDiff before={row.before} after={row.after} />
              )}
              {row.kind === "added" && (
                <Box sx={{ mt: "4px" }}>
                  <BlockRow
                    block={row.block}
                    seq={row.afterIndex + 1}
                    last
                    tone="added"
                  />
                </Box>
              )}
              {row.kind === "removed" && (
                <Box sx={{ mt: "4px" }}>
                  <BlockRow
                    block={row.block}
                    seq={row.beforeIndex + 1}
                    last
                    tone="removed"
                  />
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>
      <Box
        onClick={() => setShowAll((v) => !v)}
        sx={{
          mt: "6px",
          fontSize: "10px",
          fontWeight: 600,
          color: theme.vars.palette.Content.Weak,
          cursor: "pointer",
          letterSpacing: "0.04em",
          userSelect: "none",
        }}
      >
        {showAll ? "▾ 전체 활동지 접기" : "▸ 전체 활동지 보기"}
      </Box>
      {showAll && (
        <Box sx={{ mt: "4px" }}>
          <BlocksPreview args={args} />
        </Box>
      )}
    </Box>
  );
}

function BlocksPreview({ args }: { args: Record<string, unknown> }) {
  const [expanded, setExpanded] = useState(true);
  const blocks = asBlocks(args);
  // blocks 형태가 어긋나면(미지 도구 등) raw JSON fallback.
  if (!blocks) return <GenericArgsPreview args={args} />;

  return (
    <Box>
      <Box
        onClick={() => setExpanded((v) => !v)}
        sx={{
          fontSize: "10px",
          fontWeight: 600,
          color: theme.vars.palette.Content.Weak,
          cursor: "pointer",
          letterSpacing: "0.04em",
          mb: "4px",
          userSelect: "none",
        }}
      >
        {expanded ? "▾" : "▸"} {blocks.length}개 블록
      </Box>
      {expanded && (
        <Stack
          sx={{
            borderRadius: "6px",
            border: `1px solid ${theme.vars.palette.Border.Weak}`,
            backgroundColor: theme.vars.palette.Background.Level2,
            overflow: "hidden",
          }}
        >
          {blocks.map((block, i) => (
            <BlockRow
              key={i}
              block={block}
              seq={i + 1}
              last={i === blocks.length - 1}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}

function BlockRow({
  block,
  seq,
  last,
  tone,
}: {
  block: TaskEditorBlock;
  seq: number;
  last: boolean;
  tone?: "added" | "removed";
}) {
  const isHeader = block.type === "header";
  const actionType = block.action?.type ?? null;
  return (
    <Box
      sx={{
        p: "8px 10px",
        borderBottom: last ? 0 : `1px solid ${theme.vars.palette.Border.Weak}`,
        ...(tone === "removed" && {
          textDecoration: "line-through",
          opacity: 0.7,
        }),
      }}
    >
      {/* 메타 라인: 순번 · 헤더/본문 · action 종류 */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          mb: "6px",
          flexWrap: "wrap",
        }}
      >
        <Box
          component="span"
          sx={{
            fontFamily: MONO,
            fontSize: "10px",
            color: theme.vars.palette.Content.Weak,
            flexShrink: 0,
          }}
        >
          {seq}
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: "9px",
            fontWeight: 700,
            letterSpacing: "0.04em",
            px: "5px",
            py: "1px",
            borderRadius: "4px",
            border: `1px solid ${
              isHeader
                ? theme.vars.palette.primary.solid
                : theme.vars.palette.Border.Weak
            }`,
            color: isHeader
              ? theme.vars.palette.primary.solid
              : theme.vars.palette.Content.Weak,
            flexShrink: 0,
          }}
        >
          {isHeader ? "헤더" : "본문"}
        </Box>
        {actionType && (
          <Box
            component="span"
            sx={{
              fontFamily: MONO,
              fontSize: "10px",
              color: theme.vars.palette.primary.solid,
              flexShrink: 0,
            }}
          >
            {actionType}
          </Box>
        )}
      </Box>
      {/* 전체 본문 — 잘라내지 않고 wrap. 텍스트 굵기는 블록 타입(헤더/본문) 차등. */}
      <Stack spacing={0.5} sx={{ fontSize: "12px", lineHeight: 1.55 }}>
        <Box
          sx={{
            fontWeight: isHeader ? 700 : 400,
            color: theme.vars.palette.Content.Strong,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {block.description.content}
        </Box>
        {describeDescriptionExtra(block.description) && (
          <Box
            sx={{
              color: theme.vars.palette.Content.Weak,
              fontStyle: "italic",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {describeDescriptionExtra(block.description)}
          </Box>
        )}
        {block.action && (
          <Box
            sx={{
              mt: "2px",
              color: theme.vars.palette.primary.solid,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {describeAction(block.action)}
          </Box>
        )}
      </Stack>
    </Box>
  );
}

// description 의 type 별 부가요소 요약 (basic 은 없음 — 빈 문자열).
function describeDescriptionExtra(d: TaskEditorDescription): string {
  switch (d.type) {
    case "basic":
      return "";
    case "image":
      return "🖼 이미지";
    case "label":
      return (
        "🏷 " +
        d.pairs
          .map((p) => (p.text ? `${p.label}: ${p.text}` : p.label))
          .join(" / ")
      );
    case "reference":
      return d.reference_type === "coach_preset"
        ? "↪ 코치 프리셋: " + d.preset_text
        : "↪ 자동사고 불러오기";
    default:
      return "";
  }
}

function describeAction(a: TaskEditorActionComponent): string {
  switch (a.type) {
    case "text_input":
      return `✏ 텍스트 입력 · ${a.fields.length}칸`;
    case "button_default":
      return (
        "◻ 버튼" +
        (a.allow_multiple_selection ? " (복수)" : "") +
        ": " +
        a.options.map((o) => o.text).join(" / ")
      );
    case "button_quiz":
      return (
        "❓ 퀴즈: " +
        a.options.map((o) => (o.is_correct ? "✓ " : "") + o.text).join(" / ")
      );
    case "button_branch":
      return "⑃ 분기: " + a.options.map((o) => o.text).join(" / ");
    default:
      return "";
  }
}

// 미지 도구(레거시 write_file mock 등) — 접이식 raw JSON. 하드 크래시 방지용 fallback.
function GenericArgsPreview({ args }: { args: Record<string, unknown> }) {
  const [expanded, setExpanded] = useState(false);
  const { displayText } = expandJsonStringFields(args);
  if (!displayText || displayText === "{}") return null;
  return (
    <Box>
      <Box
        onClick={() => setExpanded((v) => !v)}
        sx={{
          fontSize: "10px",
          fontWeight: 600,
          color: theme.vars.palette.Content.Weak,
          cursor: "pointer",
          letterSpacing: "0.04em",
          mb: "4px",
          userSelect: "none",
        }}
      >
        {expanded ? "▾" : "▸"} 인자
      </Box>
      {expanded && (
        <Box
          sx={{
            p: "8px 10px",
            borderRadius: "4px",
            border: `1px solid ${theme.vars.palette.Border.Weak}`,
            backgroundColor: theme.vars.palette.Background.Level2,
            fontFamily: MONO,
            fontSize: "11px",
            lineHeight: 1.5,
            maxHeight: "240px",
            overflow: "auto",
            whiteSpace: "pre",
            ...taskEditorScrollbarSx,
          }}
        >
          {displayText}
        </Box>
      )}
    </Box>
  );
}
